"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Scale, Eye, EyeOff, UserPlus, ShieldCheck } from "lucide-react";
import { GlassCard } from "@/components/design-system/GlassCard";
import { CyberButton } from "@/components/design-system/CyberButton";
import { HolographicText } from "@/components/design-system/HolographicText";
import { useAuth } from "@/hooks/use-auth";
import { useNotificationStore } from "@/stores/notifications";

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
  "DC",
] as const;

const ROLES = [
  { value: "defense_attorney", label: "Defense Attorney", icon: Scale },
  { value: "private_investigator", label: "Private Investigator", icon: ShieldCheck },
  { value: "public_defender", label: "Public Defender", icon: Scale },
] as const;

const registerSchema = z
  .object({
    name: z.string().min(2, "Name must be at least 2 characters"),
    organizationName: z.string().min(2, "Organization name is required"),
    email: z.string().email("Enter a valid email address"),
    password: z.string().min(8, "Password must be at least 8 characters"),
    confirmPassword: z.string(),
    barNumber: z.string().optional(),
    barState: z.string().optional(),
    role: z.enum(["defense_attorney", "private_investigator", "public_defender"], {
      errorMap: () => ({ message: "Select a role" }),
    }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type RegisterFormData = z.infer<typeof registerSchema>;

export default function RegisterPage() {
  const router = useRouter();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { register: registerUser, isLoading } = useAuth();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  const selectedRole = watch("role");

  const onSubmit = async (data: RegisterFormData) => {
    setError(null);

    try {
      // Split the full name into first and last
      const nameParts = data.name.trim().split(/\s+/);
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "";

      await registerUser({
        organization_name: data.organizationName,
        email: data.email,
        password: data.password,
        first_name: firstName,
        last_name: lastName,
        bar_number: data.barNumber || undefined,
        bar_state: data.barState || undefined,
        app_role: data.role,
      });
      addNotification({ type: "analysis_complete", title: "Account Created", message: "Welcome to FrameCounsel! Your account has been created." });
      router.push("/cases");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : typeof err === "string" ? err : "Registration failed. Please try again.";
      setError(message);
      addNotification({ type: "raid_alert", title: "Registration Failed", message });
    }
  };

  return (
    <div className="space-y-6">
      {/* Logo & Title */}
      <div className="text-center space-y-3">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-neon-purple/10 border border-neon-purple/30 mb-2">
          <Scale className="w-8 h-8 text-neon-purple" />
        </div>
        <HolographicText as="h1" className="text-3xl block">
          FrameCounsel
        </HolographicText>
        <p className="text-white/50 text-sm">
          Create Your Secure Account
        </p>
      </div>

      {/* Registration Card */}
      <GlassCard variant="strong" padding="lg" neonBorder>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <div role="alert" className="p-3 rounded-lg bg-neon-red/10 border border-neon-red/30 text-neon-red text-sm">
              {error}
            </div>
          )}

          {/* Full Name */}
          <div className="space-y-1.5">
            <label
              htmlFor="name"
              className="block text-xs font-mono uppercase tracking-wider text-white/50"
            >
              Full Name
            </label>
            <input
              id="name"
              type="text"
              autoComplete="name"
              placeholder="Jane Doe, Esq."
              className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-white/20 focus:outline-none focus:border-neon-cyan/50 focus:bg-white/[0.07] transition-all duration-200 text-sm"
              {...register("name")}
            />
            {errors.name && (
              <p role="alert" className="text-neon-red text-xs mt-1">{errors.name.message}</p>
            )}
          </div>

          {/* Organization Name */}
          <div className="space-y-1.5">
            <label
              htmlFor="organizationName"
              className="block text-xs font-mono uppercase tracking-wider text-white/50"
            >
              Organization / Firm Name
            </label>
            <input
              id="organizationName"
              type="text"
              placeholder="Doe & Associates LLP"
              className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-white/20 focus:outline-none focus:border-neon-cyan/50 focus:bg-white/[0.07] transition-all duration-200 text-sm"
              {...register("organizationName")}
            />
            {errors.organizationName && (
              <p role="alert" className="text-neon-red text-xs mt-1">{errors.organizationName.message}</p>
            )}
          </div>

          {/* Email */}
          <div className="space-y-1.5">
            <label
              htmlFor="email"
              className="block text-xs font-mono uppercase tracking-wider text-white/50"
            >
              Email Address
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="counsel@lawfirm.com"
              className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-white/20 focus:outline-none focus:border-neon-cyan/50 focus:bg-white/[0.07] transition-all duration-200 text-sm"
              {...register("email")}
            />
            {errors.email && (
              <p role="alert" className="text-neon-red text-xs mt-1">{errors.email.message}</p>
            )}
          </div>

          {/* Password */}
          <div className="space-y-1.5">
            <label
              htmlFor="password"
              className="block text-xs font-mono uppercase tracking-wider text-white/50"
            >
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                placeholder="Min 8 characters"
                className="w-full px-4 py-3 pr-11 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-white/20 focus:outline-none focus:border-neon-cyan/50 focus:bg-white/[0.07] transition-all duration-200 text-sm"
                {...register("password")}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
            {errors.password && (
              <p role="alert" className="text-neon-red text-xs mt-1">{errors.password.message}</p>
            )}
          </div>

          {/* Confirm Password */}
          <div className="space-y-1.5">
            <label
              htmlFor="confirmPassword"
              className="block text-xs font-mono uppercase tracking-wider text-white/50"
            >
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              placeholder="Re-enter password"
              className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-white/20 focus:outline-none focus:border-neon-cyan/50 focus:bg-white/[0.07] transition-all duration-200 text-sm"
              {...register("confirmPassword")}
            />
            {errors.confirmPassword && (
              <p role="alert" className="text-neon-red text-xs mt-1">{errors.confirmPassword.message}</p>
            )}
          </div>

          {/* Bar Number & State (side by side) */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label
                htmlFor="barNumber"
                className="block text-xs font-mono uppercase tracking-wider text-white/50"
              >
                Bar Number
              </label>
              <input
                id="barNumber"
                type="text"
                placeholder="e.g. 12345"
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-white/20 focus:outline-none focus:border-neon-cyan/50 focus:bg-white/[0.07] transition-all duration-200 text-sm"
                {...register("barNumber")}
              />
              {errors.barNumber && (
                <p role="alert" className="text-neon-red text-xs mt-1">{errors.barNumber.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="barState"
                className="block text-xs font-mono uppercase tracking-wider text-white/50"
              >
                Bar State
              </label>
              <select
                id="barState"
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-neon-cyan/50 focus:bg-white/[0.07] transition-all duration-200 text-sm appearance-none cursor-pointer"
                {...register("barState")}
                defaultValue=""
              >
                <option value="" disabled className="bg-deep-space text-white/50">
                  State
                </option>
                {US_STATES.map((state) => (
                  <option key={state} value={state} className="bg-deep-space text-white">
                    {state}
                  </option>
                ))}
              </select>
              {errors.barState && (
                <p role="alert" className="text-neon-red text-xs mt-1">{errors.barState.message}</p>
              )}
            </div>
          </div>

          {/* Role Selection */}
          <div className="space-y-2">
            <label className="block text-xs font-mono uppercase tracking-wider text-white/50">
              Role
            </label>
            <div className="grid grid-cols-1 gap-2">
              {ROLES.map(({ value, label, icon: Icon }) => (
                <label
                  key={value}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-all duration-200
                    ${
                      selectedRole === value
                        ? "bg-neon-cyan/10 border-neon-cyan/40 text-neon-cyan"
                        : "bg-white/5 border-white/10 text-white/60 hover:border-white/20 hover:bg-white/[0.07]"
                    }
                  `}
                >
                  <input
                    type="radio"
                    value={value}
                    className="sr-only"
                    {...register("role")}
                  />
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className="text-sm font-medium">{label}</span>
                  {selectedRole === value && (
                    <div className="ml-auto w-2 h-2 rounded-full bg-neon-cyan shadow-[0_0_6px_rgba(0,255,255,0.6)]" />
                  )}
                </label>
              ))}
            </div>
            {errors.role && (
              <p role="alert" className="text-neon-red text-xs mt-1">{errors.role.message}</p>
            )}
          </div>

          {/* Submit */}
          <CyberButton
            type="submit"
            color="purple"
            fullWidth
            loading={isLoading}
            icon={<UserPlus className="w-4 h-4" />}
          >
            Create Account
          </CyberButton>

          {/* Link to login */}
          <p className="text-center text-xs text-white/40">
            Already have an account?{" "}
            <Link
              href="/login"
              className="text-neon-cyan/70 hover:text-neon-cyan transition-colors"
            >
              Sign In
            </Link>
          </p>
        </form>
      </GlassCard>

      {/* Footer */}
      <p className="text-center text-[11px] text-white/20">
        By registering, you agree to our Terms of Service and
        <br />
        acknowledge our ABA-compliant data handling practices.
      </p>
    </div>
  );
}
